import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  ArchivePayAdviceTemplateRequestDto,
  PayAdviceTemplateDetailsDto,
  PayAdviceTemplateListDto,
  SavePayAdviceTemplateRequestDto,
  SetDefaultPayAdviceTemplateRequestDto,
} from './organisationApiTypes';

const base = (businessRecordId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/pay-advice-templates`;

// The body is read even on an error status, because business refusals carry
// their operator-facing message in the JSON envelope. An unhandled server
// error is not JSON at all — ASP.NET returns a plain-text exception page —
// and parsing it used to throw first, hiding the status behind a JSON syntax
// error. Fall back to the status instead.
const parse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => null) as ApiResponse<T> | null;
  if (!response.ok) throw new Error(body?.message || `Request failed: ${response.status}`);
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable Pay Advice Template response.');
  return body.result;
};

const json = (method: 'PUT' | 'POST', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const listPayAdviceTemplates = async (
  businessRecordId: string,
  effectiveDate?: string,
): Promise<PayAdviceTemplateListDto> => {
  const url = effectiveDate ? `${base(businessRecordId)}?effectiveDate=${encodeURIComponent(effectiveDate)}` : base(businessRecordId);
  return parse(await authFetch(url));
};

export const getPayAdviceTemplate = async (
  businessRecordId: string,
  templateId: string,
  effectiveDate?: string,
): Promise<PayAdviceTemplateDetailsDto> => {
  const path = `${base(businessRecordId)}/${encodeURIComponent(templateId)}`;
  const url = effectiveDate ? `${path}?effectiveDate=${encodeURIComponent(effectiveDate)}` : path;
  return parse(await authFetch(url));
};

export const createPayAdviceTemplate = async (
  businessRecordId: string,
  request: SavePayAdviceTemplateRequestDto,
): Promise<PayAdviceTemplateDetailsDto> =>
  parse(await authFetch(base(businessRecordId), json('POST', request)));

export const updatePayAdviceTemplate = async (
  businessRecordId: string,
  templateId: string,
  request: SavePayAdviceTemplateRequestDto,
): Promise<PayAdviceTemplateDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/${encodeURIComponent(templateId)}`, json('PUT', request)));

export const archivePayAdviceTemplate = async (
  businessRecordId: string,
  templateId: string,
  request: ArchivePayAdviceTemplateRequestDto,
): Promise<PayAdviceTemplateDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/${encodeURIComponent(templateId)}/archive`, json('POST', request)));

export const setDefaultPayAdviceTemplate = async (
  businessRecordId: string,
  request: SetDefaultPayAdviceTemplateRequestDto,
): Promise<PayAdviceTemplateDetailsDto> =>
  parse(await authFetch(`${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/default-pay-advice-template`, json('PUT', request)));
